import React from 'react';
import { Link } from 'react-router-dom';
import { FaceSmileIcon } from '@heroicons/react/24/outline';
import { ReactionPayload } from '../../../generated-api';
import { EMOJI_DISPLAY, EmojiType } from '../../reactions/types';

interface MergedReactionCardProps {
  reactions: Array<{ payload: ReactionPayload; createdAt: string }>;
}

/**
 * Helper to get emoji display character from emoji code.
 * Falls back to the code itself if not found.
 */
const getEmojiDisplay = (emoji: string): string => {
  const display = EMOJI_DISPLAY[emoji as EmojiType];
  return display ? display.emoji : emoji;
};

export const MergedReactionCard: React.FC<MergedReactionCardProps> = ({ reactions }) => {
  if (reactions.length === 0) return null;

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Use the first reaction's playId and gameName (they should all be the same)
  const firstReaction = reactions[0];
  const playId = firstReaction.payload.playId;
  const gameName = firstReaction.payload.gameName;
  const latestCreatedAt = reactions[reactions.length - 1].createdAt;

  // Deduplicate reactions by playerId - keep only the latest reaction per user
  // (when a user changes their reaction, we only want to show their current one)
  const latestReactionByPlayer = new Map<string, { payload: ReactionPayload; createdAt: string }>();
  reactions.forEach((reaction) => {
    // Since reactions are in chronological order, later ones will overwrite earlier ones
    latestReactionByPlayer.set(reaction.payload.playerId, reaction);
  });
  const deduplicatedReactions = Array.from(latestReactionByPlayer.values());

  // Group deduplicated reactions by emoji
  const reactionsByEmoji: Record<string, Array<{ playerName: string; emoji: string }>> = {};
  deduplicatedReactions.forEach(({ payload }) => {
    const emoji = payload.emoji;
    if (!reactionsByEmoji[emoji]) {
      reactionsByEmoji[emoji] = [];
    }
    reactionsByEmoji[emoji].push({
      playerName: payload.playerName,
      emoji: emoji,
    });
  });

  // Get unique emojis for display
  const uniqueEmojis = Object.keys(reactionsByEmoji);
  const totalReactions = deduplicatedReactions.length;

  return (
    <Link
      data-track="feed_reaction_card"
      to={`/plays/${playId}`}
      className="block bg-surface rounded-lg shadow-sm border border-edge hover:shadow-md transition-shadow duration-200"
    >
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 bg-yellow-100 rounded-full p-2">
              <FaceSmileIcon className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-heading">
                {totalReactions} New Reaction{totalReactions > 1 ? 's' : ''}
              </p>
              <p className="text-xs text-muted">{formatRelativeTime(latestCreatedAt)}</p>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            {uniqueEmojis.map((emoji) => {
              const emojiDisplay = getEmojiDisplay(emoji);
              const count = reactionsByEmoji[emoji].length;
              return (
                <span key={emoji} className="text-2xl" title={`${count} ${emoji} reaction${count > 1 ? 's' : ''}`}>
                  {emojiDisplay}
                </span>
              );
            })}
          </div>
        </div>
        <div className="mt-3">
          <p className="text-sm text-body">
            {uniqueEmojis.map((emoji, index) => {
              const emojiDisplay = getEmojiDisplay(emoji);
              const emojiReactions = reactionsByEmoji[emoji];
              const count = emojiReactions.length;
              
              // Show first player name, and "and X others" if there are more
              const firstPlayerName = emojiReactions[0].playerName;
              const otherCount = count - 1;
              
              return (
                <span key={emoji}>
                  {index > 0 && ' • '}
                  <span className="font-medium text-yellow-600">{firstPlayerName}</span>
                  {otherCount > 0 && ` and ${otherCount} other${otherCount > 1 ? 's' : ''}`}
                  {' reacted with '}
                  <span className="text-lg">{emojiDisplay}</span>
                </span>
              );
            })}
            {' to a play of '}
            <span className="font-medium">{gameName}</span>
          </p>
        </div>
      </div>
    </Link>
  );
};

